/**
 * Pure H OS · Sprint 3 · Channel Performance.
 * Composes Card, SectionHeader, DeltaChip.
 */
import { LineChart, Instagram, Music2, Mail, Youtube, MessageSquare, Building, Handshake } from "lucide-react";
import { Card, SectionHeader, DeltaChip } from "../../components/lib";
import { CHANNELS } from "../../mocks/marketingData";

const ICON: Record<string, any> = {
  instagram: Instagram, tiktok: Music2, email: Mail, youtube: Youtube,
  sms: MessageSquare, ooh: Building, affiliate: Handshake,
};

export default function Channels() {
  const totalSpend   = CHANNELS.reduce((n, c) => n + c.spendEGP, 0);
  const totalRevenue = CHANNELS.reduce((n, c) => n + c.revenueEGP, 0);
  const blendedRoas  = totalSpend ? totalRevenue / totalSpend : 0;
  const maxBar = Math.max(...CHANNELS.map((c) => c.revenueEGP));

  return (
    <div className="space-y-6" data-testid="mkt-channels">
      <Card testId="mkt-channels-summary">
        <SectionHeader eyebrow="30-day" title="Blended performance" action={<LineChart size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Ad spend</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-ink-strong)" }}>EGP {(totalSpend / 1000).toFixed(0)}K</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Attributed revenue</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-ink-strong)" }}>EGP {(totalRevenue / 1000000).toFixed(2)}M</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Blended ROAS</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-success)" }}>{blendedRoas.toFixed(2)}×</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Active channels</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-ink-strong)" }}>{CHANNELS.length}</div>
          </div>
        </div>
      </Card>

      <Card padded={false} testId="mkt-channels-table">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Channels" title="Per-channel snapshot" />
        </div>

        <div className="p-5">
          <div className="hidden md:grid text-[10px] uppercase tracking-[0.14em] pb-2" style={{ color: "var(--ph-ink-muted)", gridTemplateColumns: `1.4fr 1fr 1fr 1fr 1fr 0.6fr` }}>
            <div>Channel</div>
            <div className="text-right">Reach</div>
            <div className="text-right">CTR</div>
            <div className="text-right">Spend</div>
            <div className="text-right">Revenue</div>
            <div className="text-right">Δ</div>
          </div>

          <div className="space-y-2">
            {CHANNELS.map((c) => {
              const Icon = ICON[c.key] || LineChart;
              const barPct = maxBar ? Math.max(2, (c.revenueEGP / maxBar) * 100) : 2;
              return (
                <div key={c.key} data-testid={`mkt-channel-${c.key}`} className="grid items-center py-2" style={{ gridTemplateColumns: `1.4fr 1fr 1fr 1fr 1fr 0.6fr`, borderTop: "1px solid var(--ph-border-subtle)" }}>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center" style={{ width: 24, height: 24, background: "var(--ph-brand-tint)", color: "var(--ph-brand-primary)", borderRadius: "999px" }}>
                      <Icon size={12} />
                    </span>
                    <div>
                      <div className="text-sm font-semibold" style={{ color: "var(--ph-ink-strong)" }}>{c.name}</div>
                      <div className="mt-1 h-1 rounded" style={{ background: "var(--ph-border-subtle)", overflow: "hidden", width: 160 }}>
                        <div style={{ width: `${barPct}%`, height: "100%", background: "var(--ph-brand-primary)" }} />
                      </div>
                    </div>
                  </div>
                  <div className="text-sm tabular-nums text-right" style={{ color: "var(--ph-ink-strong)" }}>{c.reach.toLocaleString()}</div>
                  <div className="text-sm tabular-nums text-right" style={{ color: "var(--ph-ink-default)" }}>{(c.ctr * 100).toFixed(1)}%</div>
                  <div className="text-sm tabular-nums text-right" style={{ color: "var(--ph-ink-default)" }}>EGP {(c.spendEGP / 1000).toFixed(0)}K</div>
                  <div className="text-sm tabular-nums text-right" style={{ color: "var(--ph-ink-strong)" }}>EGP {(c.revenueEGP / 1000).toFixed(0)}K</div>
                  <div className="flex justify-end"><DeltaChip delta={c.delta} trend={c.trend} /></div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>
    </div>
  );
}
